import React, {useEffect, useState} from 'react';
import ListStudentsUI from '../view/ListLearnersUI';
import {inject, observer} from 'mobx-react';
import LearnersStore from '../../store/LearnersStore';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import ConfirmationModal from '../../../../uiToolkit/ConfirmationModal';
import {listLearnersText} from '../listLearnersText';
import {toast} from 'react-toastify';
import {appText} from '../../../../assets/text/appText';
import {toastConfig} from '../../../../uiToolkit/Toast/toastConfig';
import NavigationStore from '../../../../navigation/store/NavigationStore';
import {LearnerListItem} from '../../../../data/models/Learners/Learner';
import {useClarityPageView} from '../../../../utils/useClarityPageView';

const PAGE_SIZE = 10;

interface Props {
  learnersStore?: LearnersStore;
  navigationStore?: NavigationStore;
}

const ListLearnersContainer = inject(
  'learnersStore',
  'navigationStore',
)(
  observer(({learnersStore, navigationStore}: Props) => {
    const [learners, setLearners] = useState<LearnerListItem[]>([]);
    const [pages, setPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [courses, setCourses] = useState<any[]>([]);
    const [filters, setFilters] = useState<string[] | undefined>(undefined);
    const [search, setSearch] = useState<string | undefined>('');
    const [deleteFromCoursePayload, setDeleteFromCoursPayload] = useState<{
      learnerId: string;
      courseId: string;
    }>();
    const [learnerToDeleteId, setLearnerToDeleteId] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const {organizationId} = useParams();
    const [isInitialized, setIsInitialized] = useState(false);

    useClarityPageView('/manage/learners', 'staff');

    useEffect(() => {
      fetchCourses();
    }, [learnersStore]);

    useEffect(() => {
      if (location && location.state) {
        if (location.state.filters && Array.isArray(location.state.filters)) {
          setFilters(location.state.filters);
          location.state.filters = null;
        } else if (location.state.courseName && courses.length) {
          const filterCourse = courses.find(
            x => x.title === location.state.courseName,
          );
          if (filterCourse) {
            setFilters([filterCourse.id]);
          }
          location.state.courseName = null;
        }
      }
      setIsInitialized(true);
    }, [location, location.state, courses]);

    useEffect(() => {
      if (isInitialized) {
        fetchLearners();
      }
    }, [learnersStore, currentPage, search, filters, isInitialized]);

    const fetchLearners = async () => {
      if (organizationId) {
        setIsLoading(true);
        try {
          const res = await learnersStore?.getMyLearners(
            currentPage - 1,
            organizationId,
            PAGE_SIZE,
            filters,
            search,
          );
          setLearners(res?.data || []);
          setPages(res?.pagesCount || 0);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    const onAddNewLearners = () => {
      navigate(`/manage/${organizationId}/learners/create`);
    };

    const onInitiateDeleteLearner = (learnerId: string) => {
      setLearnerToDeleteId(learnerId);
    };

    const onDeleteLearner = () => {
      if (organizationId && learnerToDeleteId && !learnersStore?.isLoading) {
        setLearnerToDeleteId(undefined);
        learnersStore!
          .deleteLearner(organizationId, learnerToDeleteId)
          .then(() => {
            navigationStore!.getOrganizationDashboardAnalytics(organizationId);
            fetchLearners();
          });
      }
    };

    const onActivateLearner = (learnerId: string) => {
      if (organizationId && !learnersStore?.isLoading) {
        learnersStore!.activateLearner(organizationId, learnerId).then(() => {
          navigationStore!.getOrganizationDashboardAnalytics(organizationId);
          fetchLearners();
        });
      }
    };

    const onPageChange = (page: number) => {
      setCurrentPage(page);
    };

    const fetchCourses = () => {
      if (organizationId) {
        learnersStore
          ?.getOrganizationCourses(organizationId)
          .then(res => setCourses(res));
      }
    };

    const handleAcceptButtonPressed = (filteredCourses: string[]) => {
      if (filteredCourses.length === 0) {
        setFilters(undefined);
      } else {
        setFilters(filteredCourses);
      }
    };

    const handleSearchKeyChange = (search: string) => {
      if (search !== '') {
        setSearch(search);
      } else {
        setSearch(undefined);
      }
    };

    const onDeleteFromCourse = (learnerId: string, courseId: string) => {
      setDeleteFromCoursPayload({learnerId, courseId});
    };

    const deleteFromCourse = () => {
      if (
        organizationId &&
        deleteFromCoursePayload &&
        !learnersStore?.isLoading
      ) {
        learnersStore!
          .removeLearnerFromTheCourse(
            organizationId,
            deleteFromCoursePayload.learnerId,
            deleteFromCoursePayload.courseId,
          )
          .then(fetchLearners);
        setDeleteFromCoursPayload(undefined);
      }
    };

    const onReEnroll = (courseId: string, learnerId: string) => {
      if (organizationId && !learnersStore?.isLoading) {
        learnersStore!
          .reEnrollLearner(organizationId, courseId, learnerId)
          .then(fetchLearners);
      }
    };

    const onCopyEmail = async (email: string) => {
      await navigator.clipboard.writeText(email);
      toast.success(appText.copiedToClipboard, toastConfig);
    };

    const onResendInvitation = (email: string) => {
      if (organizationId && !learnersStore?.isLoading) {
        learnersStore!.resendInvitation(organizationId, email);
      }
    };

    const onBack = () => {
      if (location.state?.fromPage && location.state?.courseId) {
        navigate(
          `/manage/${organizationId}/courses/${location.state.courseId}/learners`,
        );
      } else {
        navigate(`/manage/${organizationId}/courses`);
      }
    };

    return (
      <>
        <ListStudentsUI
          learners={learners}
          onAddNewLearners={onAddNewLearners}
          onDelete={onInitiateDeleteLearner}
          pages={pages}
          onPageChange={onPageChange}
          courses={courses}
          applyFiltersButton={handleAcceptButtonPressed}
          onSearchKeyChange={handleSearchKeyChange}
          onDeleteFromCourse={onDeleteFromCourse}
          onActivateLearner={onActivateLearner}
          onReEnroll={onReEnroll}
          onCopyEmail={onCopyEmail}
          onResendInvitation={onResendInvitation}
          filters={filters || []}
          onBack={onBack}
          isLoading={isLoading}
        />
        <ConfirmationModal
          shown={deleteFromCoursePayload !== undefined}
          onAccept={deleteFromCourse}
          onCancel={() => setDeleteFromCoursPayload(undefined)}
          acceptText={listLearnersText.remove}
          title={listLearnersText.removeFromCourseConfirmation}
        />
        <ConfirmationModal
          shown={learnerToDeleteId !== undefined}
          onAccept={onDeleteLearner}
          onCancel={() => setLearnerToDeleteId(undefined)}
          acceptText={listLearnersText.remove}
          title={listLearnersText.removeFromOrgConfirmation}
        />
      </>
    );
  }),
);

export default ListLearnersContainer;
